import React, { useMemo } from "react"
import DataTable from "react-data-table-component"
import { customStyles } from "../Common/DataTableStyles"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu"
import { Button } from "../../../components/ui/button"
import { MoreVertical, Eye, ViewIcon, RefreshCwIcon, Loader2 } from "lucide-react"

export const PartnerTableList = ({
    partnerships,
    tenant,
    getCompanyLogo,
    getPartnershipTypeBadge,
    getStatusBadge,
    formatDate,
    handleDetailsView,
    handleNavigateToCatalogue,
    handleConfirmationModal,
    handleResendInvitation,
    isDropdownOpen,
    setIsDropdownOpen,
}) => {
    const columns = useMemo(() => [
        {
            name: "Company",
            cell: (row) => (
                <div className="flex items-center space-x-3 py-2">
                    {getCompanyLogo(row.invitedTo, row.logo)}
                    <div>
                        <div className="font-medium text-white">{row.invitedTo}</div>
                        <div className="text-sm text-gray-400">{row.userEmail}</div>
                    </div>
                </div>
            ),
            grow: 2,
            sortable: true,
            selector: (row) => row.invitedTo,
        },
        {
            name: "Partnership Type",
            cell: (row) => getPartnershipTypeBadge(row.userType),
            sortable: true,
            selector: (row) => row.userType,
        },
        {
            name: "Invited From",
            selector: (row) => row.invitedFromTenantName,
            sortable: true,
        },
        {
            name: "Invited By",
            selector: (row) => row.invitedFromUserName,
            sortable: true,
        },
        {
            name: "Send Date",
            selector: (row) => row.createdDate,
            sortable: true,
            cell: (row) => formatDate(row.createdDate),
        },
        {
            name: "Status",
            cell: (row) => getStatusBadge(row.notificationStatus),
            sortable: true,
            selector: (row) => row.notificationStatus,
        },
        {
            name: "Actions",
            cell: (row, index) => (
                <div className="flex justify-end w-full">
                    <DropdownMenu modal={false} onOpenChange={(isOpen) => setIsDropdownOpen(isOpen ? index : null)} open={isDropdownOpen === index}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-black border-gray-800 text-white">
                            <DropdownMenuItem className="hover:bg-gray-800 focus:bg-gray-800" onClick={() => handleDetailsView(row, true)}>
                                <Eye className="h-4 w-4 mr-2" /> View Company
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                                className="hover:bg-gray-800 focus:bg-gray-800"
                                disabled={row.invitedToTenantId === tenant?.id || row.notificationStatus !== "Accept"} 
                                onClick={() => handleNavigateToCatalogue(row)}
                            >
                                <ViewIcon className="h-4 w-4 mr-2" /> View Catalogue
                            </DropdownMenuItem>
                            {row.invitedToTenantId !== tenant?.id && (row.notificationStatus !== "Accept") && (
                                <DropdownMenuItem
                                    className="hover:bg-gray-800 focus:bg-gray-800"
                                    onClick={() => {
                                        setIsDropdownOpen(null);
                                        handleConfirmationModal(true, () => handleResendInvitation(row.id));
                                    }}>
                                    <RefreshCwIcon className="h-4 w-4 mr-2" />
                                    Resend
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        },
    ], [isDropdownOpen, tenant]);

    return (
        <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
            <DataTable
                columns={columns}
                data={partnerships}
                pagination
                noDataComponent={
                    <div className="text-center py-10 text-gray-400">
                        No partnerships found
                    </div>
                }
                customStyles={customStyles}
                highlightOnHover
                pointerOnHover
                responsive
            />
        </div>
    )
}
